import { useState, useMemo, useEffect } from 'react'
import {
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  Send,
  Repeat,
  Heart,
} from 'lucide-react'
import { api, type Room, type RoomSchedule, type DonationContext } from '../lib/api'
import { formatLocalDate, formatSlotRange, estimateDonationBreakdown } from '../lib/utils'
import { RecurringBookingDialog } from './RecurringBookingDialog'

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  JPY: '¥',
  CHF: 'CHF',
  SGD: 'S$',
  HKD: 'HK$',
}

// Format time for display (24h to 12h)
function formatTime(time: string): string {
  const parts = time.split(':')
  const hour = parseInt(parts[0] || '0')
  const m = parts[1] || '00'
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${hour12}:${m} ${ampm}`
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export interface BookingSubmitData {
  roomId: string
  date: string
  startTime: string
  endTime: string
  selectedSlots: string[] // All selected time slots (for non-consecutive bookings)
  title?: string
  notes?: string
  // Guest fields (for marketplace)
  guestName?: string
  guestEmail?: string
  guestPhone?: string
  // Recurring fields (admin only)
  isRecurring?: boolean
  recurringPattern?: 'weekly' | 'monthly_day' | 'monthly_week'
  recurringEndDate?: string
  weekInterval?: number
  // Voluntary donation pledged at booking time (stripe donation mode)
  donationAmount?: number
}

// What onSubmit may return so the modal can tailor its success screen and, for
// stripe-mode voluntary donations, send the guest on to the payment page.
export interface BookingSubmitResult {
  booking?: { id: string; status: string }
}

interface UnifiedBookingModalProps {
  // Room info
  room: Room
  roomName: string
  schedules: RoomSchedule[]
  currency?: string

  // Out of hours booking permission
  canBookOutOfHours?: boolean

  // Manager (admin/approver) override: shows a toggle to reveal days/time slots outside
  // the room/building's available hours so bookings can be scheduled out of hours.
  allowOutOfHours?: boolean

  // Mode
  mode: 'member' | 'guest' | 'request' // member = signed in, guest = public booking, request = access request

  // Optional: for selecting from multiple rooms (dashboard)
  rooms?: Room[]
  onRoomChange?: (roomId: string) => void

  // Pre-filled user info (when signed in)
  userName?: string
  userEmail?: string

  // Initial values (for pre-filling)
  initialDate?: string // YYYY-MM-DD
  initialStartTime?: string // HH:MM
  initialEndTime?: string // HH:MM
  initialSlots?: string[] // Pre-selected time slots (e.g., ["14:00", "15:00", "18:00"])
  initialNotes?: string

  // Optional org slug for contexts where no current org is selected (marketplace)
  orgSlug?: string

  // Callbacks
  onClose: () => void
  onSubmit: (data: BookingSubmitData) => Promise<void | BookingSubmitResult>

  // Fetch availability function
  fetchAvailability: (
    roomId: string,
    startDate: string,
    endDate: string
  ) => Promise<{ startTime: string; endTime: string; status: string }[]>

  // Optional features
  submitButtonText?: string
  title?: string
  showRecurring?: boolean
}

export function UnifiedBookingModal({
  room,
  roomName,
  schedules,
  currency = 'GBP',
  canBookOutOfHours = false,
  allowOutOfHours = false,
  mode,
  rooms,
  onRoomChange,
  userName = '',
  userEmail = '',
  initialDate,
  initialStartTime,
  initialEndTime,
  initialSlots,
  initialNotes,
  orgSlug,
  onClose,
  onSubmit,
  fetchAvailability,
  submitButtonText,
  title = 'Request to Book',
  showRecurring = false,
}: UnifiedBookingModalProps) {
  // Calendar state - initialize with initialDate if provided
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (initialDate) {
      return new Date(initialDate + 'T00:00:00')
    }
    return new Date()
  })
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (initialDate) {
      return new Date(initialDate + 'T00:00:00')
    }
    return null
  })
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(() => {
    // Initialize with initialSlots if provided
    if (initialSlots && initialSlots.length > 0) {
      return new Set(initialSlots)
    }
    return new Set()
  })
  const [roomBookings, setRoomBookings] = useState<
    { startTime: string; endTime: string; status: string }[]
  >([])

  // Form state
  const [guestName, setGuestName] = useState(userName)
  const [guestEmail, setGuestEmail] = useState(userEmail)
  const [guestPhone, setGuestPhone] = useState('')
  const [message, setMessage] = useState(initialNotes || '')
  const [bookingTitle, setBookingTitle] = useState('')

  // Submit state
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [submitError, setSubmitError] = useState('')

  // Recurring state (admin only)
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurringAllowed, setRecurringAllowed] = useState(false)

  useEffect(() => {
    if (mode !== 'member' || (!showRecurring && !orgSlug)) return
    let cancelled = false
    api.bookings.recurringPolicy(orgSlug).then((policy) => { if (!cancelled) setRecurringAllowed(policy.allowed) }).catch(() => { if (!cancelled) setRecurringAllowed(false) })
    return () => { cancelled = true }
  }, [mode, showRecurring, orgSlug])

  // Voluntary donation state: fetched for signed-in members so the modal can offer
  // a card donation at booking time when the org uses stripe donation mode.
  const [donationContext, setDonationContext] = useState<DonationContext | null>(null)
  const [donationInput, setDonationInput] = useState('')
  const [donationNote, setDonationNote] = useState('') // extra line on the success screen
  const [createdBooking, setCreatedBooking] = useState<{ id: string; status: string } | null>(null)

  useEffect(() => {
    if (mode !== 'member') return
    let cancelled = false
    api.bookings
      .getDonationContext(orgSlug)
      .then((ctx) => {
        if (!cancelled) setDonationContext(ctx)
      })
      .catch(() => {
        // The donation prompt is optional; booking still works without it
      })
    return () => {
      cancelled = true
    }
  }, [mode, orgSlug])

  const isVoluntaryDonationGuest = mode === 'member' && !!donationContext?.voluntaryDonation
  const showDonationPrompt =
    isVoluntaryDonationGuest &&
    donationContext?.donationMode === 'stripe' &&
    donationContext.stripeReady
  const donationAmount = useMemo(() => {
    const value = Math.round(parseFloat(donationInput) * 100) / 100
    return Number.isFinite(value) && value >= 1 ? Math.min(value, 10000) : 0
  }, [donationInput])
  const donationEstimate =
    showDonationPrompt && donationAmount > 0 && donationContext
      ? estimateDonationBreakdown(donationAmount, donationContext.roomsiFeePercent)
      : null

  // Whether this user is permitted to book outside the room's available hours at all -
  // either a guest with the out-of-hours permission, or an admin/approver scheduling.
  const mayBookOutOfHours = canBookOutOfHours || allowOutOfHours

  // Out-of-hours days/slots are hidden by default and only revealed when the user opts in
  // via the toggle, so normal-hours booking stays simple.
  const [showAllHours, setShowAllHours] = useState(false)
  const showOutOfHours = mayBookOutOfHours && showAllHours

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Load availability when month or room changes
  useEffect(() => {
    const loadAvailability = async () => {
      try {
        const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
        const endOfMonth = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() + 1,
          0,
          23,
          59,
          59
        )
        const bookings = await fetchAvailability(
          room.id,
          startOfMonth.toISOString(),
          endOfMonth.toISOString()
        )
        setRoomBookings(bookings)
      } catch (err) {
        console.error('Failed to load availability:', err)
      }
    }
    loadAvailability()
  }, [currentMonth, room.id, fetchAvailability])

  // Pre-select slots if initial slots or times provided (runs once on mount)
  useEffect(() => {
    // If we have explicit slots, use them directly (non-consecutive selection support)
    if (initialSlots && initialSlots.length > 0 && selectedDate) {
      setSelectedSlots(new Set(initialSlots))
      return
    }

    // Otherwise, if we have start/end times, fill in the range between them
    if (initialStartTime && initialEndTime && selectedDate) {
      const slots = new Set<string>()
      const startParts = initialStartTime.split(':')
      const endParts = initialEndTime.split(':')

      let hour = parseInt(startParts[0] || '0')
      let min = parseInt(startParts[1] || '0')
      const endHour = parseInt(endParts[0] || '0')
      const endMin = parseInt(endParts[1] || '0')

      while (hour < endHour || (hour === endHour && min < endMin)) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`
        slots.add(timeStr)
        min += 30
        if (min >= 60) {
          min = 0
          hour++
        }
      }

      if (slots.size > 0) {
        setSelectedSlots(slots)
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Calendar navigation
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)

    const days: (Date | null)[] = []

    // Add padding for days before first of month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null)
    }

    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day))
    }

    return days
  }, [currentMonth])

  // Whether a day has an active schedule (its normal opening hours)
  const dayHasSchedule = (date: Date): boolean => {
    const dayOfWeek = date.getDay()
    return schedules.some((s) => s.dayOfWeek === dayOfWeek && s.isActive)
  }

  // Check if a day can be selected
  const hasAvailability = (date: Date): boolean => {
    if (date < today) return false
    if (showOutOfHours) return true // OOH guests / managers can book any day
    return dayHasSchedule(date)
  }

  // Get schedule for selected date
  const daySchedule = useMemo(() => {
    if (!selectedDate) return null
    const dayOfWeek = selectedDate.getDay()
    return schedules.find((s) => s.dayOfWeek === dayOfWeek && s.isActive)
  }, [selectedDate, schedules])

  // Generate time slots for selected date
  const timeSlots = useMemo(() => {
    // Out-of-hours guests / managers (toggle on) get full 24h time slots regardless of schedule
    if (showOutOfHours) {
      const slots: string[] = []
      for (let hour = 0; hour < 24; hour++) {
        for (let min = 0; min < 60; min += 30) {
          slots.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`)
        }
      }
      return slots
    }

    if (!daySchedule) return []

    const slots: string[] = []
    const openParts = (daySchedule.openTime || '09:00').split(':')
    const closeParts = (daySchedule.closeTime || '17:00').split(':')
    const openHour = parseInt(openParts[0] || '0')
    const openMin = parseInt(openParts[1] || '0')
    const closeHour = parseInt(closeParts[0] || '0')
    const closeMin = parseInt(closeParts[1] || '0')

    let hour = openHour
    let min = openMin

    while (hour < closeHour || (hour === closeHour && min < closeMin)) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`
      slots.push(timeStr)
      min += 30
      if (min >= 60) {
        min = 0
        hour++
      }
    }

    return slots
  }, [daySchedule, showOutOfHours])

  // Whether a slot falls within the day's normal opening hours (false = out of hours)
  const isSlotWithinSchedule = (time: string): boolean => {
    if (!daySchedule) return false
    const parts = time.split(':')
    const slotMin = parseInt(parts[0] || '0') * 60 + parseInt(parts[1] || '0')
    const openParts = (daySchedule.openTime || '09:00').split(':')
    const closeParts = (daySchedule.closeTime || '17:00').split(':')
    const openMin = parseInt(openParts[0] || '0') * 60 + parseInt(openParts[1] || '0')
    const closeMin = parseInt(closeParts[0] || '0') * 60 + parseInt(closeParts[1] || '0')
    return slotMin >= openMin && slotMin < closeMin
  }

  // Check if a time slot is booked
  const getSlotStatus = (time: string): 'available' | 'pending' | 'booked' => {
    if (!selectedDate) return 'available'

    const dateStr = formatLocalDate(selectedDate)
    const slotStart = new Date(`${dateStr}T${time}:00`)
    const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000)

    const overlapping = roomBookings.find((booking) => {
      const bookingStart = new Date(booking.startTime)
      const bookingEnd = new Date(booking.endTime)
      return slotStart < bookingEnd && slotEnd > bookingStart
    })

    if (overlapping?.status === 'confirmed') return 'booked'
    if (overlapping?.status === 'pending') return 'pending'
    return 'available'
  }

  // Calculate price based on selected slots
  const calculatedPrice = useMemo(() => {
    if (selectedSlots.size === 0) return 0
    const hours = selectedSlots.size * 0.5
    const rate =
      typeof room.hourlyRate === 'string' ? parseFloat(room.hourlyRate) : room.hourlyRate || 0
    return rate * hours
  }, [room, selectedSlots])

  // Get start and end time from selected slots
  const bookingTimes = useMemo(() => {
    if (selectedSlots.size === 0) return { start: '', end: '' }

    const sortedSlots = Array.from(selectedSlots).sort()
    const startTime = sortedSlots[0] || ''
    const lastSlot = sortedSlots[sortedSlots.length - 1] || ''

    // End time is 30 mins after the last slot
    const parts = lastSlot.split(':')
    const h = parseInt(parts[0] || '0')
    const m = parseInt(parts[1] || '0')
    const endMin = m + 30
    const endHour = endMin >= 60 ? h + 1 : h
    const endMinFinal = endMin >= 60 ? endMin - 60 : endMin
    const endTime = `${endHour.toString().padStart(2, '0')}:${endMinFinal.toString().padStart(2, '0')}`

    return { start: startTime, end: endTime }
  }, [selectedSlots])

  // Check if all required fields are complete
  const isFormComplete = useMemo(() => {
    // Must have date and time slots
    if (!selectedDate || selectedSlots.size === 0) return false

    // For guest/request mode, need name and email
    if (mode !== 'member') {
      if (!guestName.trim() || !guestEmail.trim()) return false
    }

    return true
  }, [selectedDate, selectedSlots, mode, guestName, guestEmail])

  // Handle date selection
  const handleDateSelect = (day: Date) => {
    setSelectedDate(day)
    setSelectedSlots(new Set())
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (mode !== 'member' && (!guestName.trim() || !guestEmail.trim())) {
      setSubmitError('Please enter your name and email')
      return
    }

    if (!selectedDate || selectedSlots.size === 0) {
      setSubmitError('Please select a date and time slots')
      return
    }

    setSubmitStatus('loading')
    setSubmitError('')

    const dateStr = formatLocalDate(selectedDate) || ''
    const sortedSlots = Array.from(selectedSlots).sort()

    const pledgedDonation = showDonationPrompt && donationAmount > 0 ? donationAmount : undefined

    try {
      const result = await onSubmit({
        roomId: room.id,
        date: dateStr,
        startTime: bookingTimes.start,
        endTime: bookingTimes.end,
        selectedSlots: sortedSlots,
        title: bookingTitle || undefined,
        notes: message || undefined,
        guestName: mode !== 'member' ? guestName.trim() : undefined,
        guestEmail: mode !== 'member' ? guestEmail.trim() : undefined,
        guestPhone: mode !== 'member' ? guestPhone.trim() : undefined,
        donationAmount: pledgedDonation,
      })

      const booking = result && result.booking ? result.booking : null
      if (booking) setCreatedBooking(booking)

      if (booking && pledgedDonation) {
        if (booking.status === 'confirmed') {
          // Booking confirmed straight away: take the guest to the secure payment page
          try {
            const { url } = await api.stripe.createDonationCheckout(booking.id, pledgedDonation)
            if (url) {
              window.location.href = url
              return
            }
          } catch {
            setDonationNote(
              "We couldn't start your donation payment. You can donate at any time from My Bookings."
            )
          }
        } else {
          setDonationNote(
            'Once your booking is approved, you can make your donation from My Bookings.'
          )
        }
      }

      setSubmitStatus('success')
    } catch (err: any) {
      setSubmitStatus('error')
      setSubmitError(err.message || 'Failed to submit')
    }
  }

  // Determine button text
  const buttonText = submitButtonText || (mode === 'request' ? 'Send Request' : 'Create Booking')

  if (isRecurring) {
    return <RecurringBookingDialog room={room} orgSlug={orgSlug} currency={currency}
      initialDate={selectedDate ? formatLocalDate(selectedDate) : undefined}
      initialStartTime={bookingTimes.start || undefined} initialEndTime={bookingTimes.end || undefined}
      initialTitle={bookingTitle} initialNotes={message}
      onClose={() => setIsRecurring(false)} onCreated={() => onClose()} />
  }

  // Success state
  if (submitStatus === 'success') {
    // When the server told us the booking's actual status, base the messaging on that
    // (an admin's booking confirms immediately; a guest's may await approval).
    const isPendingResult = createdBooking
      ? createdBooking.status === 'pending'
      : mode === 'guest' || mode === 'request'

    const successTitle =
      mode === 'request'
        ? 'Request Sent!'
        : isPendingResult
          ? 'Booking Submitted!'
          : 'Booking Created!'

    const successMessage =
      mode === 'request'
        ? "Your booking request has been sent. You'll receive an email when it's reviewed."
        : isPendingResult
          ? "Your booking has been submitted and is awaiting approval. You'll receive an email confirmation shortly."
          : 'Your booking has been confirmed.'

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{successTitle}</h3>
          <p className="text-gray-600 mb-6">{successMessage}</p>
          {donationNote && (
            <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-100 rounded-lg mb-6 text-left">
              <Heart className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">{donationNote}</p>
            </div>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal container - full height on mobile, auto on desktop */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500">{roomName}</p>
          </div>
          <button onClick={onClose} aria-label="Close booking" title="Close booking" className="p-2 hover:bg-gray-100 rounded-md transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <form id="booking-form" onSubmit={handleSubmit} className="auth-page p-6 pb-4">
            {/* Room selector (if multiple rooms) */}
            {rooms && rooms.length > 1 && onRoomChange && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Room</label>
                <select
                  value={room.id}
                  onChange={(e) => {
                    onRoomChange(e.target.value)
                    setSelectedSlots(new Set())
                  }}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                >
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Out-of-hours toggle - shown to anyone allowed to book outside available hours */}
            {mayBookOutOfHours && (
              <label className="flex items-center justify-between gap-3 mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                <span className="text-sm">
                  <span className="font-medium text-amber-900">Show out-of-hours times</span>
                  <span className="block text-xs text-amber-700">
                    Reveal times outside this room's normal opening hours
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={showAllHours}
                  onChange={(e) => {
                    setShowAllHours(e.target.checked)
                    // Hiding out-of-hours slots again clears any stale out-of-hours selections
                    if (!e.target.checked) setSelectedSlots(new Set())
                  }}
                  className="h-5 w-5 rounded border-amber-300 text-primary-600 focus:ring-primary-500"
                />
              </label>
            )}

            {/* Calendar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={prevMonth} aria-label="Previous month" title="Previous month" className="flex h-11 w-11 items-center justify-center hover:bg-gray-100 rounded-md">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-semibold text-gray-900">
                  {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <button type="button" onClick={nextMonth} aria-label="Next month" title="Next month" className="flex h-11 w-11 items-center justify-center hover:bg-gray-100 rounded-md">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
                {DAYS.map((day) => (
                  <div key={day} className="text-gray-500 py-1 font-medium">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, idx) => {
                  if (!day) return <div key={`empty-${idx}`} className="aspect-square" />

                  const available = hasAvailability(day)
                  // Selectable only because out-of-hours is being shown (no normal schedule)
                  const outOfHoursDay = available && !dayHasSchedule(day)
                  const isSelected =
                    selectedDate &&
                    day.getDate() === selectedDate.getDate() &&
                    day.getMonth() === selectedDate.getMonth() &&
                    day.getFullYear() === selectedDate.getFullYear()
                  const isToday = day.toDateString() === new Date().toDateString()

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      aria-label={day.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                      aria-pressed={!!isSelected}
                      title={outOfHoursDay ? 'Outside available hours' : undefined}
                      onClick={() => available && handleDateSelect(day)}
                      disabled={!available}
                      className={`aspect-square rounded-md text-sm font-medium transition-colors ${
                        isSelected
                          ? 'bg-gray-900 text-white'
                          : outOfHoursDay
                            ? 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-dashed border-amber-300'
                            : available
                              ? 'bg-community-soft hover:bg-teal-100 text-community-strong border border-teal-200'
                              : 'text-gray-300 cursor-not-allowed'
                      } ${isToday && !isSelected ? 'ring-2 ring-gray-900' : ''}`}
                    >
                      {day.getDate()}
                    </button>
                  )
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-green-50 border border-green-200"></span>{' '}
                  Available
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-gray-100"></span> Unavailable
                </span>
              </div>
            </div>

            {/* Time Slots */}
            {selectedDate && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {selectedDate.toLocaleDateString('en-GB', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                  {selectedSlots.size > 0 && bookingTimes.start && bookingTimes.end && (
                    <span className="text-xs text-primary-600 font-medium">
                      {formatTime(bookingTimes.start)} - {formatTime(bookingTimes.end)}
                    </span>
                  )}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-3 mb-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span> Available
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span> Pending
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span> Booked
                  </span>
                  {showOutOfHours && (
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span> Out of hours
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {timeSlots.map((time) => {
                    const status = getSlotStatus(time)
                    const isSelected = selectedSlots.has(time)
                    const isAvailable = status === 'available'
                    // Slot is bookable but outside the room's normal opening hours
                    const isOutOfHours = isAvailable && !isSlotWithinSchedule(time)

                    let slotClass = ''
                    if (isSelected) {
                      slotClass = 'bg-primary-600 text-white border border-primary-600'
                    } else if (status === 'booked') {
                      slotClass =
                        'bg-blue-50 text-blue-400 cursor-not-allowed border border-blue-200'
                    } else if (status === 'pending') {
                      slotClass =
                        'bg-yellow-50 text-yellow-600 cursor-not-allowed border border-yellow-200'
                    } else if (isOutOfHours) {
                      slotClass =
                        'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-dashed border-amber-300'
                    } else {
                      slotClass =
                        'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200'
                    }

                    return (
                      <button
                        key={time}
                        type="button"
                        title={isOutOfHours ? 'Outside available hours' : undefined}
                        onClick={() => {
                          if (!isAvailable) return
                          const newSlots = new Set(selectedSlots)
                          if (newSlots.has(time)) {
                            newSlots.delete(time)
                          } else {
                            newSlots.add(time)
                          }
                          setSelectedSlots(newSlots)
                        }}
                        disabled={!isAvailable}
                        className={`px-2 py-1.5 text-xs rounded transition-colors whitespace-nowrap ${slotClass}`}
                      >
                        {formatSlotRange(time)}
                      </button>
                    )
                  })}
                </div>

                {timeSlots.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No available times on this day
                  </p>
                )}
              </div>
            )}

            {/* Price Summary */}
            {selectedSlots.size > 0 && calculatedPrice > 0 && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {formatTime(bookingTimes.start)} - {formatTime(bookingTimes.end)} (
                    {selectedSlots.size * 0.5} hrs)
                  </span>
                  {isVoluntaryDonationGuest ? (
                    <span className="font-semibold text-green-700">Free of charge</span>
                  ) : (
                    <span className="font-semibold text-gray-900">
                      {CURRENCY_SYMBOLS[currency]}
                      {calculatedPrice.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Voluntary donation (stripe donation mode) */}
            {showDonationPrompt && selectedSlots.size > 0 && (
              <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="w-4 h-4 text-rose-500" />
                  <span className="text-sm font-semibold text-gray-900">
                    Would you like to make a donation?
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Your booking is free of charge. If you would like to support{' '}
                  {donationContext?.orgName || 'the organisation'}, you can donate by card. There
                  is no obligation.
                </p>
                <div className="flex items-center gap-2 mb-3">
                  {[5, 10, 20].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() =>
                        setDonationInput(donationInput === String(value) ? '' : String(value))
                      }
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        donationInput === String(value)
                          ? 'bg-rose-500 text-white border-rose-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-rose-300'
                      }`}
                    >
                      £{value}
                    </button>
                  ))}
                  <div className="relative flex-1 min-w-[90px]">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                      £
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      step="0.50"
                      value={donationInput}
                      onChange={(e) => setDonationInput(e.target.value)}
                      placeholder="Other"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                    />
                  </div>
                </div>
                {donationEstimate && donationContext ? (
                  <div className="text-xs text-gray-600 space-y-1 bg-white rounded-lg p-3 border border-rose-100">
                    <div className="flex justify-between">
                      <span>Your donation</span>
                      <span className="font-medium text-gray-900">
                        £{donationAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Card fee (estimated)</span>
                      <span>−£{donationEstimate.cardFee.toFixed(2)}</span>
                    </div>
                    {donationEstimate.roomsiFee > 0 && (
                      <div className="flex justify-between">
                        <span>Roomsi fee ({donationContext.roomsiFeePercent}%)</span>
                        <span>−£{donationEstimate.roomsiFee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium text-gray-900 border-t border-gray-100 pt-1">
                      <span>{donationContext.orgName || 'The organisation'} receives about</span>
                      <span>£{donationEstimate.orgReceives.toFixed(2)}</span>
                    </div>
                    <p className="text-[11px] text-gray-400 pt-1">
                      Card fees are estimates; the exact amount is set by the card provider. You
                      will be taken to a secure payment page once your booking is confirmed.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    Leave this blank if you would rather not donate this time.
                  </p>
                )}
              </div>
            )}

            {/* Guest fields (for marketplace) */}
            {mode !== 'member' && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="07123 456789"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="you@example.com"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Message (optional)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    placeholder="Any additional details..."
                  />
                </div>
              </>
            )}

            {/* Member fields (for dashboard) */}
            {mode === 'member' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Title (optional)
                  </label>
                  <input
                    type="text"
                    value={bookingTitle}
                    onChange={(e) => setBookingTitle(e.target.value)}
                    placeholder="e.g., Team Meeting"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Notes (optional)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={2}
                    placeholder="Any additional details..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                  />
                </div>
              </>
            )}

            {recurringAllowed && (
              <div className="border-t border-gray-200 pt-4 mb-4">
                <button type="button" onClick={() => setIsRecurring(true)} className="inline-flex min-h-11 items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">
                  <Repeat className="h-4 w-4" />Make a recurring booking
                </button>
              </div>
            )}

            {submitError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            )}
          </form>
        </div>

        {/* Sticky Submit Button Footer */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl">
          <button
            type="submit"
            form="booking-form"
            disabled={submitStatus === 'loading' || !isFormComplete}
            className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              isFormComplete && submitStatus !== 'loading'
                ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg shadow-primary-600/25'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {submitStatus === 'loading' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                {mode === 'request' && <Send className="w-4 h-4" />}
                {buttonText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
